/* Shared GroupedDocDialog used both from the company list and the new
   full-page company detail view. Extracted from Entreprises.jsx (Vague 91). */
import React, { useState } from 'react';
import api from '../api/client';

export default function GroupedDocDialog({ open, kind, onClose, company, learners, initial, onCreated }) {
  const isQuote = kind === 'quote';
  const [selected, setSelected] = useState(new Set(initial.learner_ids || learners.map((l) => l.id)));
  const [amount, setAmount] = useState(initial.amount_per_learner || 1200);
  const [tva, setTva] = useState(20);
  const [description, setDescription] = useState(initial.description || '');
  const [dueDate, setDueDate] = useState(initial.due_date || '');
  const [validityDays, setValidityDays] = useState(initial.validity_days || 30);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const ht = (Number(amount) || 0) * selected.size;
  const tvaRate = Math.max(0, Math.min(100, Number(tva) || 0));
  const tvaAmount = Math.round(ht * tvaRate) / 100;
  const ttc = Math.round((ht + (ht * tvaRate / 100)) * 100) / 100;

  const submit = async () => {
    setError(null);
    if (selected.size === 0) { setError('Sélectionnez au moins un apprenant'); return; }
    if (!amount || Number(amount) <= 0) { setError('Montant invalide'); return; }
    setSaving(true);
    try {
      const payload = {
        learner_ids: Array.from(selected),
        amount_per_learner: Number(amount),
        tva_rate: tvaRate,
        description,
      };
      if (isQuote) payload.validity_days = Number(validityDays) || 30;
      else payload.due_date = dueDate || null;
      const endpoint = isQuote ? 'grouped-quote' : 'grouped-invoice';
      const { data } = await api.post(`/companies/${company.id}/billing/${endpoint}`, payload);
      onCreated(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const dialogTitle = isQuote ? 'Devis groupé' : 'Facture groupée';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div data-testid={isQuote ? 'grouped-quote-dialog' : 'grouped-invoice-dialog'} onClick={(e) => e.stopPropagation()} className="w-full max-w-[520px] bg-white rounded-2xl shadow-xl">
        <div className="px-6 py-5 border-b border-[#EFF0F6]">
          <div className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5]">{company.name}</div>
          <h3 className="text-[18px] font-bold text-[#1A1B2E] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{dialogTitle}</h3>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Apprenants à facturer ({selected.size}/{learners.length})</label>
            <div className="max-h-[200px] overflow-y-auto space-y-1 border border-[#EFF0F6] rounded-xl p-2">
              {learners.map((l) => (
                <label key={l.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#EEF2FF] cursor-pointer">
                  <input type="checkbox" data-testid={`grouped-learner-${l.id}`} checked={selected.has(l.id)} onChange={() => toggle(l.id)} className="w-4 h-4 accent-[#4F46E5]" />
                  <span className="text-[12.5px] font-medium text-[#1A1B2E]">{l.firstName} {l.lastName}</span>
                  <span className="text-[11px] text-[#8E8FA5] ml-auto">{l.email}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Montant HT / apprenant (€)</label>
              <input type="number" min="0" step="0.01" data-testid="grouped-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] outline-none focus:bg-white focus:border-[#4F46E5]" />
            </div>
            <div>
              <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">TVA (%)</label>
              <input type="number" min="0" max="100" step="0.1" data-testid="grouped-tva-input" value={tva} onChange={(e) => setTva(e.target.value)} className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] outline-none focus:bg-white focus:border-[#4F46E5]" />
            </div>
            <div>
              <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">{isQuote ? 'Validité (jours)' : 'Échéance'}</label>
              {isQuote ? (
                <input type="number" min="1" max="365" data-testid="grouped-validity-input" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] outline-none focus:bg-white focus:border-[#4F46E5]" />
              ) : (
                <input type="date" data-testid="grouped-due-input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] outline-none focus:bg-white focus:border-[#4F46E5]" />
              )}
            </div>
          </div>
          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Description</label>
            <input data-testid="grouped-description-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={isQuote ? 'Offre de formation intra-entreprise…' : 'Formation intra-entreprise — Management Agile'} className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] outline-none focus:bg-white focus:border-[#4F46E5]" />
          </div>
          <div className="rounded-xl p-3 bg-[#EEF2FF] border border-[#C7D2FE] space-y-1.5">
            <div className="flex items-center justify-between text-[12.5px]"><span className="text-[#8E8FA5]">Sous-total HT</span><span className="font-semibold text-[#1A1B2E]" data-testid="grouped-ht">{ht.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span></div>
            <div className="flex items-center justify-between text-[12.5px]"><span className="text-[#8E8FA5]">TVA ({tvaRate.toFixed(1)}%)</span><span className="font-semibold text-[#1A1B2E]" data-testid="grouped-tva">{tvaAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span></div>
            <div className="flex items-center justify-between pt-1.5 border-t border-[#C7D2FE]"><span className="text-[13px] font-semibold text-[#4A4B63]">Total TTC</span><span className="text-[20px] font-bold text-[#4F46E5]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="grouped-total">{ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span></div>
          </div>
          {error && <div className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>}
        </div>
        <div className="px-6 py-4 border-t border-[#EFF0F6] flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] hover:border-[#C7D2FE]">Annuler</button>
          <button data-testid="grouped-submit-btn" onClick={submit} disabled={saving || selected.size === 0 || !amount} className="h-10 px-5 rounded-xl brand-gradient text-white text-[13px] font-semibold disabled:opacity-60">
            {saving ? 'Création…' : (isQuote ? 'Créer le devis' : 'Créer la facture')}
          </button>
        </div>
      </div>
    </div>
  );
}
